import { useMediaQuery, useTheme } from '@mui/material';
import type {
  DataGridProProps as DataGridProps,
  GridColDef,
  GridColumnVisibilityModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { DataGridPagination, ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import type { NetworkDeviceGroupDevices__Device as Device } from '../../queries/devices.generated';

export interface DeviceTableProps extends Omit<DataGridProps<Device>, 'columns'> {
  columns: GridColDef<Device>[];
  emptyHeader?: string;
  emptyDescription?: string;
  devices: readonly Device[];
  loading: boolean;
  selectedIds: GridRowSelectionModel;
  totalCount: number;
}

export const DeviceTable = ({
  columns,
  emptyHeader = 'Add Devices to this Device Group',
  emptyDescription = 'Adding Devices to this Device Group helps organize your Network.',
  devices,
  loading,
  selectedIds,
  totalCount,
  ...props
}: DeviceTableProps) => {
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      type: !isSmallAndDown,
      id: !isSmallAndDown,
      status: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  const NoRowsOverlay = () => (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      header={emptyHeader}
      description={emptyDescription}
    />
  );

  return (
    <ScrollableDataGrid
      checkboxSelection
      columns={columns}
      columnVisibilityModel={showColumns}
      hideFooter={false}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      pagination
      paginationMode="server"
      rowCount={totalCount}
      rows={devices}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        pagination: DataGridPagination,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      {...props}
    />
  );
};
